import { useState } from 'react';
import clsx from 'clsx';
import type { ConditionGroupData, ProblemListItem, CodingSuggestion, GapType } from '../types';
import { AcceptButton } from './AcceptButton';
import { EvidencePanel } from './EvidencePanel';
import { useSession } from '../context/SessionContext';

interface Props {
  group: ConditionGroupData;
  expandedSuggestionId: Set<string> | string | null;
  onToggleExpand: (id: string) => void;
  onAccept: (id: string) => Promise<void>;
  onReject: (id: string) => Promise<void>;
  onEdit: (id: string) => void;
  onUndo: (id: string) => void;
  actionLoading: Record<string, boolean>;
}

const GAP_COLORS: Record<GapType, { bg: string; text: string; border: string; label: string }> = {
  suspect_new:     { bg: 'bg-suspect-badge', text: 'text-suspect', border: 'border-suspect-border', label: 'Suspecting Code Gap' },
  opportunity:     { bg: 'bg-opportunity-badge', text: 'text-opportunity', border: 'border-opportunity-border', label: 'Recaptured Opportunity' },
  recapture:       { bg: 'bg-recapture-badge', text: 'text-recapture', border: 'border-recapture-border', label: 'Recaptured Existing' },
  recapture_stale: { bg: 'bg-stale-badge', text: 'text-stale', border: 'border-stale-border', label: 'Suspecting Emerging' },
  over_claimed:    { bg: 'bg-overclaimed-badge', text: 'text-overclaimed', border: 'border-overclaimed-border', label: 'Recaptured Risk' },
};

export function ConditionGroup({ group, expandedSuggestionId, onToggleExpand, onAccept, onReject, onEdit, onUndo, actionLoading }: Props) {
  const { launch } = useSession();
  const [collapsed, setCollapsed] = useState(false);

  const hasOverClaimed = group.suggestions.some(s => s.gapType === 'over_claimed');
  const acceptedCount = group.suggestions.filter(s => s.status === 'accepted').length;
  const headerLabel = group.hccCategory
    ? `${group.hccDescription ?? group.hccCategory}`
    : group.suggestions[0]?.icdDescription ?? group.problemListItems[0]?.icdDescription ?? 'Other';

  return (
    <div className={clsx(
      'border rounded-lg overflow-hidden',
      hasOverClaimed ? 'border-red-200' : 'border-gray-200',
    )}>
      {/* Group header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className={clsx(
          'w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors',
          hasOverClaimed ? 'bg-red-50 hover:bg-red-100' : 'bg-gray-50 hover:bg-gray-100',
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs text-gray-400">{collapsed ? '▶' : '▼'}</span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-800 truncate">{headerLabel}</span>
              {group.hccCategory && (
                <span className="text-xs font-mono bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded flex-shrink-0">
                  {group.hccCategory}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-xs text-gray-400">
            {group.problemListItems.length + group.suggestions.length} codes
          </span>
          {acceptedCount > 0 && (
            <span className="text-xs font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
              {acceptedCount} accepted
            </span>
          )}
        </div>
      </button>

      {!collapsed && (
        <div className="divide-y divide-gray-100">
          {/* Existing problem list items */}
          {group.problemListItems.map((p) => (
            <ProblemListRow key={p.icdCode} item={p} />
          ))}

          {/* RAAPID suggestions */}
          {group.suggestions.map((s) => {
            const isExpanded = expandedSuggestionId instanceof Set ? expandedSuggestionId.has(s.id) : expandedSuggestionId === s.id;
            const isOnProblemList = group.problemListItems.some(
              pl => pl.icdCode === s.icdCode,
            );
            return (
              <div key={s.id}>
                <SuggestionRow
                  suggestion={s}
                  isExpanded={isExpanded}
                  isOnProblemList={isOnProblemList}
                  onToggleExpand={() => onToggleExpand(s.id)}
                  onAccept={() => onAccept(s.id)}
                  onReject={() => onReject(s.id)}
                  onEdit={() => onEdit(s.id)}
                  onUndo={() => onUndo(s.id)}
                  isLoading={actionLoading[s.id] ?? false}
                />
                {isExpanded && s.evidence.length > 0 && (
                  <EvidencePanel evidence={s.evidence} clinicalReasoning={s.clinicalReasoning} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProblemListRow({ item }: { item: ProblemListItem }) {
  return (
    <div className="px-3 py-2 flex items-center justify-between bg-white">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full flex-shrink-0">
          ✓ On List
        </span>
        <code className="text-xs font-mono text-gray-500 flex-shrink-0">{item.icdCode}</code>
        <span className="text-sm text-gray-700 truncate">{item.icdDescription}</span>
      </div>
      <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
        Since {new Date(item.onsetDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
      </span>
    </div>
  );
}

function SuggestionRow({
  suggestion: s,
  isExpanded,
  isOnProblemList,
  onToggleExpand,
  onAccept,
  onReject,
  onEdit,
  onUndo,
  isLoading,
}: {
  suggestion: CodingSuggestion;
  isExpanded: boolean;
  isOnProblemList: boolean;
  onToggleExpand: () => void;
  onAccept: () => Promise<void>;
  onReject: () => Promise<void>;
  onEdit: () => void;
  onUndo: () => void;
  isLoading: boolean;
}) {
  const gap = GAP_COLORS[s.gapType];
  const isNewlyAccepted = s.status === 'accepted';

  return (
    <div className={clsx(
      'px-4 py-3',
      isNewlyAccepted ? 'bg-emerald-50/50' : 'bg-white',
    )}>
      {/* Row 1: badges + action */}
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={clsx('text-xs font-bold px-1.5 py-0.5 rounded-full border', gap.bg, gap.text, gap.border)}>
            {gap.label}
          </span>
          {isOnProblemList && (
            <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
              On Problem List
            </span>
          )}
          {isNewlyAccepted && (
            <span className="text-xs font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full">
              NEW
            </span>
          )}
        </div>
        <div className="flex-shrink-0">
          <AcceptButton
            status={s.status}
            gapType={s.gapType}
            isLoading={isLoading}
            onAccept={onAccept}
            onReject={onReject}
            onEdit={onEdit}
            onUndo={onUndo}
          />
        </div>
      </div>

      {/* Row 2: ICD code + description */}
      <button
        onClick={onToggleExpand}
        className="flex items-baseline gap-2 flex-wrap text-left w-full"
      >
        <span className="font-mono font-bold text-lg text-gray-900 tracking-wide flex-shrink-0">
          {s.icdCode}
        </span>
        <span className="text-sm font-semibold text-gray-800 leading-snug">
          {s.icdDescription}
        </span>
        {s.evidence.length > 0 && (
          <span className="text-xs text-gray-400 flex-shrink-0">
            {isExpanded ? '▲' : `▼ ${s.evidence.length} evidence`}
          </span>
        )}
      </button>

      {s.justification && (
        <p className="text-xs text-gray-600 mt-1 leading-relaxed">{s.justification}</p>
      )}
    </div>
  );
}
